/**
 * 根据 rangeTypes 生成 antd DatePicker.RangePicker 的 ranges 配置。
 * 供 CDatePicker、CDateTimeRangePicker 共用。
 */

import moment from 'moment';

/**
 * @param {object} params
 * @param {string[]} params.rangeTypes 预设类型 key 列表
 * @param {number} [params.limitDays] 与各项跨度联动，超出则跳过该项
 * @param {Record<string, { label: string, value: object[] }>} params.rangesCache
 *        可变的按 typeName 缓存，与原先组件内 this._data.ranges 行为一致
 * @param {boolean} [params.clipToDayBoundary] 为 true 时（日期时间范围）将每项起止规范到自然日边界
 * @returns {Record<string, object[]>} 每项为 [moment, moment]
 */
export function createPickerRangePresets({
    rangeTypes,
    limitDays,
    rangesCache,
    clipToDayBoundary = false,
}) {
    const ranges = {};
    for (let typeName of rangeTypes) {
        if (typeof rangesCache[typeName] === 'undefined') {
            switch (typeName) {
                case 'today':
                    rangesCache[typeName] = {
                        label: '今天',
                        value: [moment(), moment()],
                    };
                    break;
                case 'yesterday':
                    rangesCache[typeName] = {
                        label: '昨天',
                        value: [
                            moment().subtract(1, 'day'),
                            moment().subtract(1, 'day'),
                        ],
                    };
                    break;
                case 'curWeek':
                    if (limitDays && limitDays < 7) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '本周',
                        value: [moment().startOf('week'), moment().endOf('week')],
                    };
                    break;
                case 'lastWeek':
                    if (limitDays && limitDays < 7) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '上周',
                        value: [
                            moment().subtract(1, 'week').startOf('week'),
                            moment().subtract(1, 'week').endOf('week'),
                        ],
                    };
                    break;
                case 'curMonth':
                    if (limitDays && limitDays < 30) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '本月',
                        value: [moment().startOf('month'), moment().endOf('month')],
                    };
                    break;
                case 'lastMonth':
                    if (limitDays && limitDays < 30) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '上月',
                        value: [
                            moment().subtract(1, 'months').startOf('month'),
                            moment().subtract(1, 'months').endOf('month'),
                        ],
                    };
                    break;
                case 'curQuarter':
                    if (limitDays && limitDays < 90) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '本季',
                        value: [
                            moment().startOf('quarter'),
                            moment().endOf('quarter'),
                        ],
                    };
                    break;
                case 'lastQuarter':
                    if (limitDays && limitDays < 90) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '上季',
                        value: [
                            moment().subtract(1, 'quarter').startOf('quarter'),
                            moment().subtract(1, 'quarter').endOf('quarter'),
                        ],
                    };
                    break;
                case 'nearly2':
                        if (limitDays && limitDays < 2) {
                            break;
                        }
                        rangesCache[typeName] = {
                            label: '最近2天',
                            value: [moment().subtract(1, 'day'), moment()],
                        };
                        break;
                case 'nearly7':
                    if (limitDays && limitDays < 7) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近7天',
                        value: [moment().subtract(6, 'day'), moment()],
                    };
                    break;
                case 'nearly30':
                    if (limitDays && limitDays < 30) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近30天',
                        value: [moment().subtract(29, 'day'), moment()],
                    };
                    break;
                case 'nearly60':
                    if (limitDays && limitDays < 60) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近60天',
                        value: [moment().subtract(59, 'day'), moment()],
                    };
                    break;
                case 'nearly90':
                    if (limitDays && limitDays < 90) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近90天',
                        value: [moment().subtract(89, 'day'), moment()],
                    };
                    break;
                case 'nearly4W':
                    if (limitDays && limitDays < 28) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近4周',
                        value: [
                            moment().subtract(3, 'week').startOf('week'),
                            moment().endOf('week'),
                        ],
                    };
                    break;
                case 'nearly12W':
                    if (limitDays && limitDays < 84) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近12周',
                        value: [
                            moment().subtract(11, 'week').startOf('week'),
                            moment().endOf('week'),
                        ],
                    };
                    break;
                case 'nearly3M':
                    if (limitDays && limitDays < 90) {
                        break;
                    }
                    rangesCache[typeName] = {
                        label: '最近3个月',
                        value: [
                            moment().subtract(2, 'months').startOf('month'),
                            moment().endOf('month'),
                        ],
                    };
                    break;
                default:
                    break;
            }
        }
        if (rangesCache[typeName]) {
            const entry = rangesCache[typeName];
            if (clipToDayBoundary) {
                const [m0, m1] = entry.value;
                ranges[entry.label] = [m0.clone().startOf('day'), m1.clone().endOf('day')];
            } else {
                ranges[entry.label] = entry.value;
            }
        }
    }
    return ranges;
}
